import type { PageContent, ContentSection, ContentStatement } from '../types/content'
import ShareableHeading from './ShareableHeading'
import ShareableStatement from './ShareableStatement'
import './ContentRenderer.css'

interface Props {
  content: PageContent
  /** Extra CSS class applied to the root wrapper */
  className?: string
}

/**
 * ContentRenderer — converts a PageContent object (loaded from YAML) into JSX.
 *
 * Rules:
 *  - section.shareable: true  → heading rendered as <ShareableHeading>
 *  - statement.shareable: true → rendered as <ShareableStatement> (requires statement.id)
 *  - section.items            → rendered as <ul> (non-shareable bullet list)
 */
export default function ContentRenderer({ content, className = '' }: Props) {
  return (
    <div className={`content-renderer ${className}`.trim()}>
      {content.title && <h1 className="cr-page-title">{content.title}</h1>}
      {content.description && <p className="cr-page-desc">{content.description}</p>}

      {content.sections.map(section => (
        <Section key={section.id} section={section} />
      ))}
    </div>
  )
}

function Section({ section }: { section: ContentSection }) {
  const level = section.level ?? 2

  return (
    <section id={section.id} className="cr-section">
      {section.heading && (
        section.shareable
          ? <ShareableHeading id={section.id} as={`h${level}` as 'h1'}>{section.heading}</ShareableHeading>
          : createElement(level, section.heading)
      )}

      {section.statements?.map((stmt, i) => (
        <Statement key={stmt.id ?? i} stmt={stmt} />
      ))}

      {section.items && section.items.length > 0 && (
        <ul className="cr-list">
          {section.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Statement({ stmt }: { stmt: ContentStatement }) {
  if (stmt.shareable && stmt.id) {
    return (
      <ShareableStatement id={stmt.id} type={stmt.type}>
        {stmt.text}
      </ShareableStatement>
    )
  }
  const Tag = stmt.type === 'quote' ? 'blockquote' : 'p'
  return <Tag className={`cr-plain cr-${stmt.type ?? 'paragraph'}`}>{stmt.text}</Tag>
}

/** Helper: render a plain heading at the given level without JSX switch. */
function createElement(level: number, text: string) {
  const Tag = `h${Math.min(Math.max(level, 1), 6)}` as 'h1'
  return <Tag className="cr-heading">{text}</Tag>
}
